package com.exura.pages.planCreator

import androidx.compose.runtime.*
import com.exura.components.Icon
import com.exura.components.loading.Loader
import com.exura.components.pageTypography.GeneralCardEntry
import com.exura.components.snackbar.SnackbarEmitter
import com.exura.features.plandefinition.domain.builder.plan.BasePlanBuilderNew
import com.exura.features.plandefinition.domain.builder.plan.PlanBuildersNew
import com.exura.features.plandefinition.domain.model.plan.BasePlan
import com.exura.features.plandefinition.domain.model.plan.PlanType
import com.exura.features.plandefinition.usecase.GetTreatmentPlanUseCase
import com.exura.features.plandefinition.usecase.GetVersionContentUseCase
import com.exura.navigation.Route
import com.exura.pages.planCreator.components.*
import com.exura.ui.*
import com.exura.util.injectAndRemember
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*


@Layout(".components.layouts.NavigableLayout")
@Page(Route.PLAN_CREATOR)
@Composable
fun PlanCreatorPage() {
    val pageContext = rememberPageContext()
    val coroutineScope = rememberCoroutineScope()

    val getTreatmentPlanUseCase = injectAndRemember<GetTreatmentPlanUseCase>()
    val getVersionContentUseCase = injectAndRemember<GetVersionContentUseCase>()

    val snackbarEmitter = SnackbarEmitter()

    var planBuilder by remember { mutableStateOf<BasePlanBuilderNew<out BasePlan>?>(null) }
    var viewMode by remember { mutableStateOf(ViewMode.EDIT) }

    var displayMode by remember { mutableStateOf(DisplayMode.TEXT) }
    var selectedSeriesIndex by remember { mutableStateOf<Int?>(null) }
    var selectedCollectionIndex by remember { mutableStateOf<Int?>(null) }
    var showPlanTypeSelection by remember { mutableStateOf(false) }

    var isLoading by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var hasInitialized by remember { mutableStateOf(false) }

    var sourceId by remember { mutableStateOf<String?>(null) }
    var planType by remember { mutableStateOf<PlanType?>(null) }

    LaunchedEffect(Unit) {
        val params = pageContext.route.queryParams


        isLoading = true
        errorMessage = null

        try {
            viewMode = when (params[Route.Params.VIEW_MODE]?.uppercase()) {
                ViewMode.EDIT.name -> ViewMode.EDIT
                ViewMode.VIEW.name -> ViewMode.VIEW
                else -> ViewMode.VIEW
            }

            displayMode = when (params[Route.Params.DISPLAY_MODE]?.uppercase()) {
                DisplayMode.GRAPHICS.name -> DisplayMode.GRAPHICS
                DisplayMode.TEXT.name -> DisplayMode.TEXT
                else -> DisplayMode.TEXT
            }

            selectedSeriesIndex = params[Route.Params.SERIES_INDEX]?.toIntOrNull()
            selectedCollectionIndex = params[Route.Params.COLLECTION_INDEX]?.toIntOrNull()

            // Handle plan loading/creation
            val planId = params[Route.Params.PLAN_ID]
            val planTypeParam = params[Route.Params.PLAN_TYPE]

            if (planId.isNullOrEmpty()) {
                if (planTypeParam.isNullOrEmpty()) {
                    showPlanTypeSelection = true
                } else try {
                    val readPlanType = PlanType.valueOf(planTypeParam.uppercase())
                    planType = readPlanType
                    planBuilder = when (readPlanType) {
                        PlanType.PREPLAN -> PlanBuildersNew.preparationPlan().apply { markSaved() }
                        PlanType.POSTPLAN -> PlanBuildersNew.treatmentPlan().apply { markSaved() }
                    }
                } catch (e: IllegalArgumentException) {
                    console.warn("Invalid planType parameter: $planTypeParam")
                    showPlanTypeSelection = true
                }
            } else {
                getTreatmentPlanUseCase(planId).onSuccess { version ->
                    val plan = getVersionContentUseCase(version)
                    planBuilder = PlanBuildersNew.fromPlan(plan)
                    if (params[Route.Params.PLAN_TYPE] != plan.planType.name) {
                    }
                    sourceId = planId
                    planType = plan.planType
                }.onFailure { error ->
                    errorMessage = "Failed to load plan: ${error.message}"
                    showPlanTypeSelection = true
                }
            }
        } catch (e: Exception) {
            errorMessage = "Error processing URL parameters: ${e.message}"
            showPlanTypeSelection = true
        } finally {
            isLoading = false
            hasInitialized = true
        }
    }

    LaunchedEffect(sourceId, planType, viewMode, displayMode, selectedSeriesIndex, selectedCollectionIndex) {
        if (hasInitialized) {
        }
    }
    when {
        isLoading -> {
            Loader()
        }

        errorMessage != null -> Box(
            modifier = Modifier.fillMaxSize(),
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                text = errorMessage ?: "Unknown error",
                modifier = Modifier.classNames(TextStyles.medium24).color(ExuraColors.ARSENIC)
            )
        }

        planBuilder != null -> {
            PlanCanvasContainer(
                pageContext = pageContext,
                coroutineScope = coroutineScope,
                planBuilder = planBuilder!!,
                snackbarEmitter = snackbarEmitter,
                viewMode = viewMode, // Pass the initial view mode
                onViewModeChange = { viewMode = it },
                displayMode = displayMode,
                onDisplayModeChange = { displayMode = it },
                sourceId = sourceId,
                selectedSeriesIndex = selectedSeriesIndex,
                selectedCollectionIndex = selectedCollectionIndex,
                onIndexChange = { cInd, sInd ->
                    selectedSeriesIndex = sInd
                    selectedCollectionIndex = cInd
                },
                onSourceIdChange = { newSourceId ->
                    sourceId = newSourceId
                }
            )
        }

        else -> {
            PlanTypeSelectionView(
                onPlanTypeSelected = { selectedPlanType ->
                    coroutineScope.launch {
                        try {
                            planBuilder = when (selectedPlanType) {
                                PlanType.PREPLAN -> PlanBuildersNew.preparationPlan()
                                PlanType.POSTPLAN -> PlanBuildersNew.treatmentPlan()
                            }
                            planType = selectedPlanType
                        } catch (e: Exception) {
                            errorMessage = "Błąd kontrukcji planu: ${e.message}"
                        }
                    }
                }
            )
        }
    }
}


@Composable
private fun PlanTypeSelectionView(
    onPlanTypeSelected: (PlanType) -> Unit
) {
    Box(
        modifier = Modifier.fillMaxSize().backgroundColor(Colors.White),
        contentAlignment = Alignment.Center
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(32.px)
        ) {
            // Header
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(12.px)
            ) {
                SpanText(
                    text = "Stwórz nowy plan",
                    modifier = Modifier.classNames(TextStyles.medium32).color(ExuraColors.ARSENIC)
                )

                SpanText(
                    text = "Wybierz typ planu, który chcesz utworzyć",
                    modifier = Modifier.classNames(TextStyles.normal18).color(ExuraColors.ARSENIC2)
                )
            }

            // Plan type options
            Row(
                horizontalArrangement = Arrangement.spacedBy(32.px),
                verticalAlignment = Alignment.Top
            ) {
                PlanTypeCard(
                    title = "Plan Przygotowawczy",
                    description = "Poproś pacjenta o wykonanie instrukcji, przestrzeganie zaleceń czy monitorowanie kluczowych aspektów zdrowia przed zaplanowaną wizytą",
                    icon1Src = Assets.IconsUntiled.PROCEED,
                    icon2Src = Assets.IconsUntiled.CALENDAR_CLEAN,
                    accentColor = ExuraColors.BLUE,
                    onClick = { onPlanTypeSelected(PlanType.PREPLAN) }
                )

                PlanTypeCard(
                    title = "Plan Terapeutyczny",
                    description = "Wskaż pacjentowi co należy robić po wizycie lub zabiegu, aby efekty dobranej terapii były jak najlepsze",
                    icon1Src = Assets.IconsUntiled.CALENDAR_CLEAN,
                    icon2Src = Assets.IconsUntiled.PROCEED,
                    accentColor = ExuraColors.BLUE,
                    onClick = { onPlanTypeSelected(PlanType.POSTPLAN) }
                )
            }
        }
    }
}

@Composable
private fun PlanTypeCard(
    title: String,
    description: String,
    icon1Src: String,
    icon2Src: String,
    accentColor: ExuraRgba,
    onClick: () -> Unit
) {
    GeneralCardEntry(
        modifier = Modifier
            .onClick { onClick() }
            .width(300.px)
            .height(300.px)
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(24.px),
            verticalArrangement = Arrangement.spacedBy(16.px),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Icon composition
            Row(
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Icon(
                    iconSrc = icon1Src,
                    size = Dimens.mainIconSizeDefault,
                    color = accentColor
                )
                Icon(
                    iconSrc = icon2Src,
                    size = Dimens.mainIconSizeDefault,
                    color = accentColor
                )
            }

            // Title
            SpanText(
                text = title,
                modifier = Modifier
                    .classNames(TextStyles.medium20)
                    .color(ExuraColors.ARSENIC)
            )

            // Description
            SpanText(
                text = description,
                modifier = Modifier
                    .classNames(TextStyles.normal14)
                    .color(ExuraColors.ARSENIC2)
                    .lineHeight(1.4)
            )
        }
    }
}
